/**
 * Lazy-loaded Sentry utilities to reduce initial bundle size
 */

import * as React from 'react'
import type * as SentryReact from '@sentry/react'
import type { Integrations as SentryIntegrations } from '@sentry/tracing'
import type { History } from 'history'

let sentryModule: typeof SentryReact | null = null
let sentryIntegrationsModule: typeof SentryIntegrations | null = null
let initPromise: Promise<void> | null = null

/**
 * Lazily loads and initializes Sentry
 */
export async function initSentry(history: History): Promise<void> {
  // Return existing initialization if already in progress or complete
  if (initPromise) {
    return initPromise
  }

  initPromise = (async () => {
    try {
      // Dynamically import Sentry modules
      const [sentry, { Integrations }] = await Promise.all([
        import('@sentry/react'),
        import('@sentry/tracing')
      ])

      sentryModule = sentry
      sentryIntegrationsModule = Integrations

      // Initialize Sentry
      sentry.init({
        dsn: 'https://8640a39841f24f8b91189ee00204f193@o1277188.ingest.sentry.io/6474587',
        integrations: [
          new Integrations.BrowserTracing({
            routingInstrumentation: sentry.reactRouterV5Instrumentation(history),
          }),
        ],
        attachStacktrace: true,
        environment: window.env ?? 'dev',
        enabled: (window.env ?? 'dev') !== 'dev',
        tracesSampleRate: 1.0,
        autoSessionTracking: false,
        release: window.SENTRY_RELEASE,
      })
    } catch (error) {
      console.error('Failed to initialize Sentry:', error)
    }
  })()

  return initPromise
}

/**
 * Get the loaded Sentry module (returns null if not yet loaded)
 */
export function getSentryModule(): typeof SentryReact | null {
  return sentryModule
}

/**
 * Lazily load Sentry module
 */
export async function loadSentryModule(): Promise<typeof SentryReact | null> {
  if (sentryModule) {
    return sentryModule
  }

  try {
    sentryModule = await import('@sentry/react')
    return sentryModule
  } catch (error) {
    console.error('Failed to load Sentry module:', error)
    return null
  }
}

/**
 * Set user context in Sentry (lazy-loads if needed)
 */
export async function setSentryUser(user: {
  id?: string
  imagekitId?: string
  clientNumber?: string
  email?: string
}): Promise<void> {
  const sentry = await loadSentryModule()
  if (sentry) {
    sentry.setUser(user)
  }
}

/**
 * Capture an exception in Sentry (lazy-loads if needed)
 */
export async function captureSentryException(
  error: unknown,
  context?: Record<string, unknown>,
): Promise<void> {
  const sentry = await loadSentryModule()
  if (sentry) {
    sentry.captureException(error, context ? { extra: context } : undefined)
  }
}

/**
 * Lazily applies Sentry profiler to a component after Sentry loads
 * Falls back to the original component if Sentry is not loaded
 */
export function withLazySentryProfiler<P extends object>(
  Component: React.ComponentType<P>,
  componentName?: string
): React.ComponentType<P> {
  const WrappedComponent = (props: P) => {
    const [ProfiledComponent, setProfiledComponent] = React.useState<React.ComponentType<P> | null>(null)

    React.useEffect(() => {
      // Load Sentry and apply profiler
      loadSentryModule().then((sentry) => {
        if (sentry) {
          const Profiled = sentry.withProfiler(Component, { name: componentName })
          setProfiledComponent(() => Profiled)
        }
      }).catch((err) => {
        console.error('Failed to apply Sentry profiler:', err)
      })
    }, [])

    // Use profiled component once loaded, otherwise use original
    const ComponentToRender = ProfiledComponent || Component
    return React.createElement(ComponentToRender, props)
  }

  WrappedComponent.displayName = `withLazySentryProfiler(${componentName || Component.displayName || Component.name || 'Component'})`

  return WrappedComponent
}
